export type GetToken = () => Promise<string | null>

export class ApiError extends Error {
  readonly status: number
  readonly detail: string

  constructor(status: number, detail: string) {
    super(detail)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
  }
}

function apiUrl(path: string): string {
  const base = import.meta.env.VITE_API_URL
  if (!base) {
    return path
  }
  const origin = base.startsWith('http') ? base : `https://${base}`
  return `${origin.replace(/\/$/, '')}${path}`
}

function snakeToCamel(key: string): string {
  return key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

function camelToSnake(key: string): string {
  return key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function camelize<T>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((item) => camelize(item)) as T
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        snakeToCamel(key),
        camelize(nested),
      ]),
    ) as T
  }
  return value as T
}

function decamelize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map((item) => decamelize(item))
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, nested]) => [
        camelToSnake(key),
        decamelize(nested),
      ]),
    )
  }
  return value
}

function fallbackDetail(response: Response): string {
  if (response.status === 0 || response.type === 'opaqueredirect') {
    return "Couldn't complete the request. Please try again."
  }
  if (response.status === 401) return 'Sign in to continue.'
  if (response.status === 403) return "You don't have permission to do that."
  if (response.status === 404) return 'Not found.'
  if (response.status === 409) return "That's already been done."
  if (response.status === 429) return 'Too many requests. Try again later.'
  if (response.status >= 500)
    return 'Something went wrong on our side. Try again.'
  return response.statusText.trim() || `Request failed (${response.status})`
}

export async function readDetail(response: Response): Promise<string> {
  try {
    const data: unknown = await response.json()
    if (!isPlainObject(data)) {
      return fallbackDetail(response)
    }
    const detail = data.detail
    if (typeof detail === 'string' && detail.trim()) {
      return detail
    }
    if (Array.isArray(detail)) {
      const joined = detail
        .map((item) =>
          isPlainObject(item) && typeof item.msg === 'string'
            ? item.msg
            : JSON.stringify(item),
        )
        .join('; ')
      if (joined) {
        return joined
      }
    }
  } catch {
    // body was empty or not JSON
  }
  return fallbackDetail(response)
}

export async function apiFetch(
  path: string,
  options: {
    method?: string
    body?: unknown
    getToken?: GetToken
    redirect?: RequestRedirect
    query?: Record<string, string | undefined>
  } = {},
): Promise<Response> {
  const headers = new Headers()
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }

  const token = options.getToken ? await options.getToken() : null
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const params = new URLSearchParams()
  if (options.query) {
    for (const [key, value] of Object.entries(options.query)) {
      if (value) {
        params.set(key, value)
      }
    }
  }
  const queryString = params.toString()
  const url = apiUrl(queryString ? `${path}?${queryString}` : path)

  return fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body:
      options.body === undefined
        ? undefined
        : JSON.stringify(decamelize(options.body)),
    redirect: options.redirect ?? 'follow',
  })
}

export async function readJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    throw new ApiError(response.status, await readDetail(response))
  }
  return camelize<T>(await response.json())
}
